<template>
  <section class="spv-info-page" v-if="spvInfo">
    <SmallLoader v-if="loading" />
    <template v-else>
      <!-- Header with back button -->
      <div class="spv-info__back">
        <BackBtn />
        <span class="spv-info__back-text">Go back to SPVs</span>
      </div>

      <!-- SPV Header -->
      <div class="spv-info__header">
        <div class="spv-info__details">
          <div class="spv-info__title-section">
            <h1 class="spv-info__title">{{ spvInfo.name }}</h1>
            <p class="spv-info__managed">Managed by {{ spvManager }}</p>
          </div>
          <div class="spv-info__meta">
            <div class="spv-info__meta-item">
              <span class="spv-info__label">Country</span>
              <span class="spv-info__value">{{ spvInfo.country }}</span>
            </div>
            <div class="spv-info__meta-item">
              <span class="spv-info__label">Date of Creation</span>
              <span class="spv-info__value">{{ formatISODate(spvInfo.createdAt) }}</span>
            </div>
          </div>
        </div>
        <router-link
          :to="{ name: 'LinkSpv', params: { id: spvInfo._id } }"
          class="spv-info__link-btn"
        >
          Link this SPV to a deal
        </router-link>
      </div>

      <!-- Document Section -->
      <div class="spv-info__content">
        <div class="spv-info__section">
          <h2 class="spv-info__section-title">SPV Document</h2>
          <div class="spv-info__document">
            <DocumentViewer :doc="{ name: spvInfo.name, url: spvInfo.url }" />
          </div>
        </div>

        <!-- Linked Deals -->
        <div class="spv-info__section spv-info__section--deals">
          <h2 class="spv-info__section-title">Deals linked to this SPV</h2>
          <div class="spv-info__deals" v-if="spvDeals && spvDeals.length > 0">
            <DealCard
              v-for="deal in spvDeals"
              :key="deal._id"
              :deal="deal"
              componentType="button"
              @click="$router.push({ name: 'DealInfo', params: { dealId: deal._id } })"
            />
          </div>
          <div class="spv-info__empty" v-else>
            <div class="spv-info__empty-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </div>
            <p class="spv-info__empty-text">No Deals have been linked to this SPV yet.</p>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import BackBtn from "@/components/general/BackBtn.vue";
import { useRoute } from "vue-router";
import { formatISODate } from "@/utils/helpers";
import DocumentViewer from "@/components/general/DocumentViewer.vue";
import DealCard from "@/components/deals/DealCard.vue";
import SmallLoader from "@/components/notification/SmallLoader.vue";

const route = useRoute();
const store = useStore();

const loading = computed(() => store.getters["loading"]);
const spvInfo = computed(() => store.getters["Spv/spvInfo"]);
const orgName = computed(() => store.state.organisationModule.organisationDetails.name);

const spvManager = computed(() =>
  spvInfo.value.should_manage_spv ? "GetEquity" : orgName.value
);

const deals = computed(() => store.getters["Deals/dealRequests"]);
const spvDeals = computed(() => {
  return (
    deals.value &&
    deals.value.filter((deal) => deal.spv === spvInfo.value._id)
  );
});

const fetchDeals = async () => {
  const query = { page: 1, limit: 1000 };
  await store.dispatch("Deals/getDealRequests", query);
};

const getSpvInfo = async () => {
  const id = route.params.id;
  await store.dispatch("Spv/fetchSpvRequest", id);
};

getSpvInfo();
fetchDeals();
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-page: #F8FAFC;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;

.spv-info-page {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 16px;
  min-height: 100vh;
  box-sizing: border-box;

  @media (min-width: 1536px) {
    max-width: 1200px;
  }
}

.spv-info {
  &__back {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 24px;
    padding-top: 16px;

    &-text {
      font-size: 14px;
      color: $text-muted;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 32px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  &__details {
    flex: 1;
  }

  &__title-section {
    padding-bottom: 16px;
    border-bottom: 1px solid $border-color;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 4px;
  }

  &__managed {
    font-size: 14px;
    color: $text-muted;
    margin: 0;
  }

  &__meta {
    display: flex;
    gap: 48px;

    @media (max-width: 480px) {
      gap: 24px;
    }
  }

  &__meta-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-size: 13px;
    color: $text-muted;
  }

  &__value {
    font-size: 15px;
    font-weight: 500;
    color: $text-primary;
  }

  &__link-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 20px;
    background: $btn-primary;
    color: white;
    border: none;
    border-radius: 7px;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: background 0.2s ease;

    &:hover {
      background: $btn-primary-hover;
      color: white;
    }
  }

  &__content {
    background: $bg-card;
    border: 1px solid $border-color;
    border-radius: 10px;
    padding: 32px;

    @media (max-width: 640px) {
      padding: 20px;
    }
  }

  &__section {
    &-title {
      font-size: 16px;
      font-weight: 500;
      color: $text-primary;
      margin: 0 0 16px;
    }

    &--deals {
      margin-top: 40px;
    }
  }

  &__document {
    max-width: 642px;
  }

  &__deals {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 12px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 1024px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__empty {
    text-align: center;
    padding: 40px 20px;

    &-icon {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: $bg-subtle;
      color: $text-muted;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 12px;
    }

    &-text {
      font-size: 14px;
      color: $text-muted;
      margin: 0;
    }
  }
}
</style>
